<template>
  <div>
    <b-breadcrumb :items="breadcrumbItems"></b-breadcrumb>

    <h4 qid="maintenance-create-rr-title">
      {{$t('maintenance.retention_rules.create.title')}}
    </h4>

    <div class="card">
      <div class="card-body">
        <div class="form-centered">
          <b-alert v-if="error.length"
                   show
                   variant="danger"
                   qid="create-rr-error"
          >
            {{ error }}
          </b-alert>
          <b-form @submit="formSubmit" qid="create-rr-form">
            <div class="form-container">
              <b-form-group>
                <label qid="create-rr-name-label">
                  {{ $t('maintenance.retention_rules.create.name_label') }}
                </label>
                <b-form-input
                  qid="create-rr-form-name"
                  type="text"
                  v-model="rrForm.name"
                  required
                  :maxLength="255"
                  :placeholder="$t('maintenance.retention_rules.create.name_placeholder')"
                ></b-form-input>
              </b-form-group>
              <b-form-group>
                <label qid="create-rr-description-label">
                  {{ $t('maintenance.retention_rules.create.description_label') }}
                </label>
                <b-form-textarea
                  qid="create-rr-form-description"
                  v-model="rrForm.description"
                  rows="6"
                  max-rows="6"
                  :placeholder="$t('maintenance.retention_rules.create.description_placeholder')"
                ></b-form-textarea>
              </b-form-group>
              <b-form-group :class="{'multiselect--show-input': timeLimitInput.length}">
                <label qid="edit-rr-subjects-label">
                  {{ $t('maintenance.retention_rules.create.time_limit_label') }}
                </label>
                <multiselect
                    v-model="rrForm.time_limit"
                    :options="timeLimitItems"
                    :placeholder="$t('maintenance.retention_rules.create.time_limit_placeholder')"
                    :preserveSearch="true"
                    :select-label="$t('system.dropdown_select_label')"
                    :selected-label="$t('system.dropdown_selected_label')"
                    :deselect-label="$t('system.dropdown_deselect_label')"
                    @search-change="handleTimeLimitInput"
                    label="time_limit"
                    qid="create-rr-form-time-limit"
                >
                  <template slot="noResult" slot-scope="props">
                    {{ $t('maintenance.retention_rules.create.time_limit_no_results') }}
                  </template>
                </multiselect>
              </b-form-group>
              <b-form-group>
                <label qid="create-rr-attachments-label">
                  {{ $t('maintenance.retention_rules.create.attachments') }}
                  &nbsp;
                  <span class="text-muted">({{ $t('attachments.file_size_label') }})</span>
                </label>
                <br>
                <p v-for="file in getFiles" :key="file.name">{{ file.name }}</p>
                <label class="btn btn-secondary cursor-pointer" qid="create-rr-attachments-button">
                  <input type="file"
                         name="fileupload"
                         value="fileupload"
                         multiple
                         id="file-rr"
                         @change="onFileChange"
                         @click="resetFile"
                  >
                  <span>
                    <font-awesome-icon icon="cloud-upload-alt" />
                    {{ $t('attachments.upload_button') }}
                  </span>
                </label>
              </b-form-group>
              <b-form-group>
                <label qid="create-rr-link-label">
                  {{ $t('maintenance.retention_rules.create.links_label') }}
                </label>
                <p v-for="(link, index) in rrForm.links">
                  <span class="float-right text-muted cursor-pointer"
                        @click="removeLinkItem(index)"
                        qid="create-rr-link-remove"
                  >
                    <font-awesome-icon icon="trash-alt" />
                  </span>
                  <a :href="link.url" target="_blank" qid="create-p-link-item">{{ link.name ? link.name : link.url }}</a>
                </p>
                <div>
                  <label class="mt-2">{{ $t('system.link') }}</label>
                  <b-form-input
                    type="text"
                    aria-describedby="inputGroup"
                    v-model="inputLink"
                    :placeholder="$t('maintenance.third_parties.create.links_placeholder')"
                    qid="create-p-links"
                  ></b-form-input>
                  <label class="mt-2">{{ $t('table.name') }}</label>
                  <b-form-input
                    type="text"
                    aria-describedby="inputGroup"
                    v-model="inputLinkName"
                    :placeholder="inputLink ? inputLink : $t('table.name')"
                    qid="create-p-links"
                  ></b-form-input>
                </div>
                <span class="btn btn-link cursor-pointer float-right"
                      @click="handleLink"
                      qid="create-rr-add-link"
                >
                  <font-awesome-icon icon="plus"/> {{ $t('system.add_link') }}
                </span>
              </b-form-group>
            </div>
            <div class="text-right">
              <b-button class="mt-3"
                        variant="secondary"
                        @click="goToList"
                        qid="create-rr-cancel-button"
              >
                {{ $t('maintenance.retention_rules.create.cancel') }}
              </b-button>
              <b-button type="submit"
                        class="mt-3 ml-3"
                        variant="success"
                        :disabled="buttonDisabled"
                        qid="create-rr-submit-button"
              >
                <span v-if="buttonDisabled" class="mr-1">
                  <font-awesome-icon icon="spinner" size="sm" class="fa-spin"/>
                </span>
                {{ $t('maintenance.retention_rules.create.submit') }}
              </b-button>
            </div>
          </b-form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import piincHttp from '@/util/configureAxios.js'
  import Datepicker from 'vuejs-datepicker';

  export default {
    name: 'CreateRetentionRule',
    data: function() {
      return {
        breadcrumbItems: [
          {
            text: this.$t('dashboard.title'),
            to: '/'
          },
          {
            text: this.$t('maintenance.title'),
            to: '/maintenance'
          },
          {
            text: this.$t('maintenance.retention_rules.title'),
            to: '/maintenance/retention-rules'
          },
          {
            text: this.$t('maintenance.retention_rules.create.title'),
            active: true
          }
        ],
        rrForm: {
          name: '',
          description: '',
          time_limit: [],
          client:'',
          links: []
        },
        error: '',
        inputLink: '',
        inputLinkName: '',
        selectedFiles: [],
        timeLimitItems: [],
        timeLimitInput: '',
        buttonDisabled: false
      }
    },
    computed: {
      getClient() {
        return this.$store.state.client
      },
      getFiles() {
        return this.selectedFiles
      }
    },
    watch: {
      // Watch when client changes in store and trigger api reload
      getClient: function () {
        this.loadLimits();
      }
    },
    methods: {
      goToList() {
        this.$router.push('/maintenance/retention-rules')
      },
      resetFile() {
        let imagefile = document.querySelector('#file-rr');
        imagefile.value = '';
      },
      handleTimeLimitInput(input) {
        this.timeLimitInput = input;
      },
      onFileChange() {
        let files = document.querySelector('#file-rr');
        // in order for v-for to re render, we need to set array to empty
        this.selectedFiles = [];
        // And then push item by item into that array
        for (var i = 0; i < files.files.length; i++) {
          this.selectedFiles.push(files.files[i])
        }
      },
      handleLink() {
        // If there's a link in input, add it to links array
        if (this.inputLink !== '') {
          if (!this.inputLink.startsWith('http')) {
            this.inputLink = 'http://' + this.inputLink
          }
          this.rrForm.links.push({url: this.inputLink, name: this.inputLinkName})
          // Reset link input form
          this.inputLink = ''
          this.inputLinkName = ''
        }
      },
      removeLinkItem(index) {
        // Remove link item from the links array
        this.rrForm.links.splice(index, 1);
      },
      formSubmit(evt) {
        let formData = new FormData();
        let files = document.querySelector('#file-rr');
        let sizeError = false;

        // Call handle link function to gather link from link input
        this.handleLink()

        // Keep the reference in a variable
        // to use it inside function called when promise returned
        let self = this;
        evt.preventDefault();

        // Append list of attachments to FormData
        for (var i = 0; i < files.files.length; i++) {
          // Equal to 10 MB
          if (files.files[i].size > 10485760) {
            sizeError = true;
          }
          formData.append("files[]", files.files[i]);
        }

        // Append list of links to FormData
        for (var i = 0; i < this.rrForm.links.length; i++) {
          formData.append("links[]", JSON.stringify(this.rrForm.links[i]));
        }

        // Additional check - if number_of_subjects is selected, append it to form data
        if (this.rrForm.time_limit && this.rrForm.time_limit.time_limit) {
          formData.append("time_limit", this.rrForm.time_limit.time_limit);
        } else {
          if (this.timeLimitInput && this.timeLimitInput.length) {
            formData.append("time_limit", this.timeLimitInput);
          }
        }

        formData.append("name", this.rrForm.name);
        formData.append("description", this.rrForm.description);
        formData.append("client", this.getClient.slug);

        if (sizeError) {
          this.error = this.$t('system.file_size')
        } else {
          this.buttonDisabled = true;
          // Post FormData object to API
          piincHttp.post('/retention-rules', formData, {
            headers: {
              'Content-Type': 'multipart/form-data'
            }
          }).then(function(response) {
            self.$toastr('success', self.$t('maintenance.retention_rules.create.success'))
            self.buttonDisabled = false;
            self.goToList();
          }, function(error) {
            if (error.status !== 200) {
              self.buttonDisabled = false;
              self.error = error.data.message;
              if (error.data.errors['files.0']) {
                self.error += ' ' + error.data.errors['files.0'][0]
              }
            }
          })
        }
      },
      loadLimits() {
        let self = this;
        piincHttp.get('retention-rules/limits', { params:
          {
            client: this.getClient.slug
          }
        }).then(function(response) {
          self.timeLimitItems = response.data
          if (self.timeLimitItems && !self.timeLimitItems.length) {
            self.timeLimitItems.push({time_limit: self.$t('system.no_results'), $isDisabled: true})
          }
        }, function() {});
      }
    },
    components: {

    },
    created () {
      if (this.getClient && this.getClient.slug) {
        this.loadLimits();
      }
    }
  }
</script>
